import React from "react";
import { Link } from "react-router-dom";

const Testimonials = () => {
  const leftPoints = [
    "Personal attention — advice shaped around your circumstances, priorities and goals.",
    "Transparent fees — clear explanations of costs from the outset, with any changes discussed with you.",
    "Dedicated support — regular updates, straightforward answers and professional guidance at every stage.",
  ];

  const reasons = [
    "Leading immigration experts",
    "All areas of UK immigration law",
    "Family Law Expert",
    "Free Initial Assessment",
    "Refusals, appeals, bail & deportation",
    "Lawyers, solicitors & barristers",
    "Proven track record of success",
    "Fixed fees, no hidden costs",
    "Transparent at every stage",
    "Convenient central office locations",
  ];

  return (
    <section id="why-choose-us" className="bg-[#EEF2F6] py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Column: What sets us apart */}
          <div className="lg:col-span-5 space-y-6">
            <h2 className="text-3xl sm:text-4xl lg:text-4xl font-bold text-[rgb(26,36,58)] leading-tight tracking-tight">
              Distinguished by care. <br />
              Defined by{" "}
              <span className="italic font-serif font-normal text-[#C9A04A]">
                commitment.
              </span>
            </h2>

            <p className="text-slate-600 text-justify text-sm sm:text-base leading-relaxed">
              Immigration and family matters can shape your future and the lives of those closest to you. At Blackhorse Law, we listen carefully, understand your priorities and develop a legal strategy tailored to your circumstances. From your first consultation to the conclusion of your matter, we provide thoughtful advice, thorough preparation and dedicated representation.
            </p>

            <ul className="space-y-3.5 pt-2">
              {leftPoints.map((point, idx) => {
                const separator = point.includes("—")
                  ? "—"
                  : point.includes("-")
                    ? "-"
                    : null;
                const [title, ...rest] = separator
                  ? point.split(separator)
                  : [point];
                const description = rest.join(separator);

                return (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#C9A04A] text-white flex items-center justify-center shrink-0 mt-0.5">
                      <svg
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <span className="text-xs sm:text-sm text-slate-700 leading-snug">
                      {separator ? (
                        <>
                          <strong className="font-bold text-[rgb(26,36,58)]">
                            {title.trim()}
                          </strong>{" "}
                          {separator} {description.trim()}
                        </>
                      ) : (
                        point
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right Column: Why Clients Choose Us Card */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl border border-slate-100">
              <span className="inline-block text-[11px] sm:text-xs font-bold tracking-widest text-slate-400 uppercase mb-5">
                WHY CLIENTS CHOOSE US
              </span>

              {/* 10 Reason Badges in 2 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {reasons.map((reason, idx) => (
                  <div
                    key={idx}
                    className="bg-[#F8FAFC] rounded-xl p-3 sm:p-3.5 border border-slate-100 flex items-center gap-3 transition-colors hover:bg-slate-100/70"
                  >
                    <span className="w-5 h-5 rounded-full bg-[#C9A04A]/15 text-[#C9A04A] flex items-center justify-center shrink-0">
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <span className="text-xs sm:text-sm font-semibold text-[rgb(26,36,58)]">
                      {reason}
                    </span>
                  </div>
                ))}
              </div>

              {/* Bottom Action Bar */}
              <div className="pt-6 mt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <span className="text-xs sm:text-sm font-semibold text-[rgb(26,36,58)] text-center sm:text-left">
                  Our specialists are ready to help you
                </span>

                <div className="flex flex-wrap items-center gap-3 justify-center">
                  <a
                    href="tel:+4401216306635"
                    className="bg-[rgb(26,36,58)] hover:bg-[#C9A04A] text-white px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-2 transition duration-200 shadow-sm"
                  >
                    <svg
                      className="w-4 h-4 text-[#C9A04A] group-hover:text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span>+44 (0) 121 630 6635</span>
                  </a>

                  <Link
                    to="/contact"
                    className="border border-slate-300 hover:border-[rgb(26,36,58)] hover:bg-[rgb(26,36,58)] hover:text-white text-[rgb(26,36,58)] px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition duration-200"
                  >
                    <span>Contact us</span>
                    <span>&rarr;</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
